import { TagCategory } from 'apprise-core/category';
import { useT } from 'apprise-frontend-core/intl/language';
import { useForm } from 'apprise-frontend-core/utils/form';
import { Button, ButtonRow } from 'apprise-ui/button/button';
import { Form } from 'apprise-ui/form/form';
import { GroupBox } from 'apprise-ui/groupbox/groupbox';
import { RouteGuard } from 'apprise-ui/link/routeguard';
import { Topbar } from 'apprise-ui/topbar/topbar';
import { Explainer } from 'apprise-ui/utils/explainer';
import producer from "immer";
import React from 'react';
import { CategoryLabel } from './label';
import { useCategoryModel } from './model';
import { useCategoryStore } from './store';


import { useTagModules } from 'apprise-frontend-tags/tag/modules';
import { useDrawer } from 'apprise-ui/drawer/drawer';
import { Label } from 'apprise-ui/label/label';
import { FaSort } from 'react-icons/fa';
import { utils } from 'apprise-core/utils';


export const useReorderCategories = (selectedType: string | undefined) => {

    const t = useT()

    const modules = useTagModules()

    const cats = useCategoryStore()

    const { Drawer, openSet } = useDrawer()

    const distinctTypes = utils().dedup(cats.allCategories().map(c => c.type))

    // if all categories have the same type. we consider it selected.
    const type = selectedType ?? (distinctTypes.length === 1 ? distinctTypes[0] : undefined)

    const canReorder = !!type && (cats.allCategoriesOf(type).filter(c => c.properties.field.enabled).length > 1)

    const button = canReorder ? <Button
        tip={t(`tag.category_reorder_${canReorder ? 'enabled' : 'disabled'}_tip`)}
        tipDelay={1}
        enabled={canReorder}
        onClick={() => openSet(true)}>
        <Label enabled={canReorder} icon={<FaSort />} title={t('tag.category_reorder')} />

    </Button> : null

    // unmount on close is easier than resetting form
    const drawer = <Drawer unmountOnClose width={400} icon={<FaSort />} title={t('tag.category_reorder_title')}>
        {type && <ReorderCategories type={type} name={t(modules.lookup(type)?.plural!)} />}
    </Drawer>

    return { drawer, button }
}

export type ReorderProps = React.PropsWithChildren<{

  type: string
  name: string

}>

export const ReorderCategories = (props: ReorderProps) => {

  const t = useT();

  const store = useCategoryStore();
  const { ordinalComparator } = useCategoryModel()

  const { type, name } = props

  const ordered = store.allCategoriesOf(type).sort(ordinalComparator).filter(c => c.properties.field.enabled)

  const form = useForm(ordered)

  // assigns ordinals, saves and resets form.
  const saveCategories = () => Promise.resolve(form.edited.map(updateOrdinal)).then(store.saveCategories).then(saved => form.reset.to(saved).quietly())
 
  // (functionally) updates the field ordinal of a category (using an immer proxy for convenience).
  const updateOrdinal  = (category: TagCategory, i:number) => producer( (m: TagCategory) => { 
    
    m.properties.field.ordinal=i}
    
  )(category)

  const revertBtn = <Button enabled={form.dirty} onClick={form.reset.toInitial.confirm}>{t('tag.category_reorder_revert')}</Button>

  const saveBtn = <Button type='primary' enabled={form.dirty} onClick={saveCategories}>{t('tag.category_reorder_save')}</Button>

  const renderCategory = (category: TagCategory) => {

    const enabled = !!category.properties.field.enabled 

    return <GroupBox.Card dot key={category.id} enabled={enabled}>
      <CategoryLabel tip={enabled? null : t('tag.category_reorder_card_disabled_tip')} enabled={enabled} category={category} />
    </GroupBox.Card>
  }

  return <>

    <Topbar>
      <ButtonRow>
        {saveBtn}
        {revertBtn}
      </ButtonRow>
    </Topbar>

    <Explainer>{t('tag.category_reorder_explainer', { name })}</Explainer>

    <Form>

      <GroupBox noAdd noRemove
        disableIf={c => !c.properties.field.enabled}
        render={renderCategory}
        onChange={categories => form.set.to(categories!)}>

        {form.edited}

      </GroupBox>

    </Form>

    <RouteGuard when={form.dirty} />


  </>

}